import type { ZoneDTO } from "../api/client";
import type { FilterConditionDTO } from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";

const ALL_VALUE = "__all__"; // Radix Select forbids value=""

type ProvincesFilterProps = {
  zones: ZoneDTO[];
};

export function ProvincesFilter({ zones }: ProvincesFilterProps) {
  const nav = useNavigate({ from: "/app/geo/provinces" });
  const search = useSearch({ from: "/app/geo/provinces" }) as {
    limit?: number;
    offset?: number;
    sort?: Array<{ field: string; dir: "asc" | "desc" }> | string;
    filters?: FilterConditionDTO[] | string;
  };

  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const nameLoFilter = findCondition(filters, "nameLo");
  const zoneFilter = findCondition(filters, "zoneId");
  const enabledFilter = findCondition(filters, "enabled");

  const [searchValue, setSearchValue] = useState<string>(
    (nameLoFilter?.value as string) || ""
  );
  const [zoneValue, setZoneValue] = useState<string>(
    (zoneFilter?.value as string) || ""
  );
  const [enabledValue, setEnabledValue] = useState<string>(
    enabledFilter?.value === true
      ? "true"
      : enabledFilter?.value === false
      ? "false"
      : ""
  );

  useEffect(
    () => setSearchValue((nameLoFilter?.value as string) || ""),
    [nameLoFilter?.value]
  );
  useEffect(
    () => setZoneValue((zoneFilter?.value as string) || ""),
    [zoneFilter?.value]
  );
  useEffect(
    () =>
      setEnabledValue(
        enabledFilter?.value === true
          ? "true"
          : enabledFilter?.value === false
          ? "false"
          : ""
      ),
    [enabledFilter?.value]
  );

  const debounced = useDebounceCallback((val: string) => {
    let nextFilters = removeConditions(filters, "nameLo") ?? [];
    if (val) {
      nextFilters = upsertCondition(nextFilters, {
        field: "nameLo",
        op: "contains",
        value: val,
      });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      } as any,
    });
  }, 400);

  const applyZone = (val: string) => {
    const effective = val === ALL_VALUE ? "" : val;
    setZoneValue(effective);
    let nextFilters = effective
      ? upsertCondition(filters, {
          field: "zoneId",
          op: "eq",
          value: effective,
        })
      : removeConditions(filters, "zoneId") ?? [];
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      } as any,
    });
  };

  const applyEnabled = (val: string) => {
    const effective = val === ALL_VALUE ? "" : val;
    setEnabledValue(effective);
    let nextFilters =
      effective === ""
        ? removeConditions(filters, "enabled") ?? []
        : upsertCondition(filters, {
            field: "enabled",
            op: "eq",
            value: effective === "true",
          });
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      } as any,
    });
  };

  const hasFilter = Boolean(
    nameLoFilter?.value || zoneFilter?.value || enabledFilter?.value
  );

  const zoneOptions = [
    { label: "ທຸກໂຊນ", value: ALL_VALUE },
    ...zones.map((z) => ({ label: z.code, value: z.id })),
  ];

  const enabledOptions = [
    { label: "ທຸກສະຖານະ", value: ALL_VALUE },
    { label: "ເປີດ", value: "true" },
    { label: "ປິດ", value: "false" },
  ];

  return (
    <div
      className="mb-4 flex flex-wrap items-center gap-4 px-2"
      data-tourid="geo-provinces-filter"
    >
      <Input
        placeholder="ຄົ້ນຫາຊື່ (ລາວ)..."
        defaultValue={searchValue}
        onChange={(e) => debounced(e.target.value)}
        className="h-8 sm:max-w-2xs"
      />
      <SimpleSelect
        placeholder="ໂຊນ"
        className="h-8 w-[160px]"
        value={zoneValue || ALL_VALUE}
        onValueChange={applyZone}
        options={zoneOptions}
      />
      <SimpleSelect
        placeholder="ສະຖານະ"
        className="h-8 w-[140px]"
        value={enabledValue || ALL_VALUE}
        onValueChange={applyEnabled}
        options={enabledOptions}
      />
      {hasFilter && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            nav({
              search: {
                ...search,
                offset: 0,
                filters: undefined,
              } as any,
            })
          }
        >
          ລ້າງຕົວກອງ
        </Button>
      )}
    </div>
  );
}
